import React from 'react';
import { Select, Button, Tooltip, Divider, Modal, Input, Space, message } from 'antd';
import {
  BoldOutlined,
  ItalicOutlined,
  UnderlineOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  InfoCircleOutlined,
  CodeOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuoteRight } from '@fortawesome/free-solid-svg-icons';

const MenuBar = ({ editor }) => {
  const [tooltipModalVisible, setTooltipModalVisible] = React.useState(false);
  const [tooltipText, setTooltipText] = React.useState('');
  const [linkModalVisible, setLinkModalVisible] = React.useState(false);
  const [linkUrl, setLinkUrl] = React.useState('');
  const [messageApi, contextHolder] = message.useMessage();

  if (!editor) {
    return null;
  }

  const handleTooltipClick = () => {
    // Check if there is any selected text first
    const { from, to } = editor.state.selection;
    const selectedText = from !== to;

    // If no text is selected, show error message
    if (!selectedText) {
      messageApi.error(
        "First write '<tooltip>' where you want it to appear and select this text; then click the tooltip button."
      );
      return;
    }

    // Check if the selected text already has a tooltip
    const hasTooltip = editor.isActive('tooltip');

    if (hasTooltip) {
      // If tooltip exists, offer edit or remove options
      Modal.confirm({
        title: 'Tooltip Options',
        content: 'What would you like to do with this tooltip?',
        okText: 'Edit',
        cancelText: 'Remove',
        onOk: () => {
          // Get current tooltip text if available
          const currentTooltip = editor.getAttributes('tooltip');
          if (currentTooltip && currentTooltip.title) {
            setTooltipText(currentTooltip.title);
          } else {
            setTooltipText('');
          }
          setTooltipModalVisible(true);
        },
        onCancel: () => {
          editor.chain().focus().unsetTooltip().run();
          messageApi.success('Tooltip removed successfully');
        }
      });
    } else {
      // If no tooltip exists, open modal to add one
      setTooltipText('');
      setTooltipModalVisible(true);
    }
  };

  const handleTooltipOk = () => {
    // Apply the tooltip mark to the selected text
    editor
      .chain()
      .focus()
      .setTooltip({ title: tooltipText || 'Tooltip' })
      .run();
    setTooltipModalVisible(false);
    setTooltipText('');
    messageApi.success('Tooltip added successfully');
  };

  const handleTooltipCancel = () => {
    setTooltipModalVisible(false);
    setTooltipText('');
  };

  const handleLinkClick = () => {
    const { from, to } = editor.state.selection;
    const selectedText = from !== to;

    if (!selectedText) {
      messageApi.error('Please select some text first to add or remove a link');
      return;
    }

    const hasLink = editor.isActive('link');

    if (hasLink) {
      Modal.confirm({
        title: 'Link Options',
        content: 'What would you like to do with this link?',
        okText: 'Edit',
        cancelText: 'Remove',
        onOk: () => {
          const currentLink = editor.getAttributes('link');
          if (currentLink && currentLink.href) {
            setLinkUrl(currentLink.href);
          } else {
            setLinkUrl('');
          }
          setLinkModalVisible(true);
        },
        onCancel: () => {
          editor.chain().focus().unsetLink().run();
          messageApi.success('Link removed successfully');
        }
      });
    } else {
      setLinkUrl('');
      setLinkModalVisible(true);
    }
  };

  const handleLinkOk = () => {
    editor
      .chain()
      .focus()
      .setLink({ href: linkUrl || '#' })
      .run();

    setLinkModalVisible(false);
    setLinkUrl('');
    messageApi.success('Link added successfully');
  };

  const handleLinkCancel = () => {
    setLinkModalVisible(false);
    setLinkUrl('');
  };

  return (
    <>
      {contextHolder}
      <Space className="menu-bar" direction="horizontal" wrap>
        <Select
          value={
            editor.isActive('heading', { level: 1 })
              ? 'h1'
              : editor.isActive('heading', { level: 2 })
              ? 'h2'
              : editor.isActive('heading', { level: 3 })
              ? 'h3'
              : 'paragraph'
          }
          onChange={(value) => {
            if (value === 'paragraph') {
              editor.chain().focus().setParagraph().run();
            } else if (value === 'h1') {
              editor.chain().focus().setHeading({ level: 1 }).run();
            } else if (value === 'h2') {
              editor.chain().focus().setHeading({ level: 2 }).run();
            } else if (value === 'h3') {
              editor.chain().focus().setHeading({ level: 3 }).run();
            }
          }}
          style={{ width: 150 }}
          options={[
            { value: 'paragraph', label: 'Normal text' }
            // { value: 'h3', label: 'Heading 3' },
            // { value: 'h2', label: 'Heading 2' },
            // { value: 'h1', label: 'Heading 1' }
          ]}
        />
        <Divider type="vertical" />

        <Button.Group>
          <Tooltip title="Bold">
            <Button
              onClick={() => editor.chain().focus().toggleBold().run()}
              type={editor.isActive('bold') ? 'primary' : 'default'}
              icon={<BoldOutlined />}
            />
          </Tooltip>
          <Tooltip title="Italic">
            <Button
              onClick={() => editor.chain().focus().toggleItalic().run()}
              type={editor.isActive('italic') ? 'primary' : 'default'}
              icon={<ItalicOutlined />}
            />
          </Tooltip>
          <Tooltip title="Underline">
            <Button
              onClick={() => editor.chain().focus().toggleUnderline().run()}
              type={editor.isActive('underline') ? 'primary' : 'default'}
              icon={<UnderlineOutlined />}
            />
          </Tooltip>
          <Tooltip title="Strikethrough">
            <Button
              onClick={() => editor.chain().focus().toggleStrike().run()}
              type={editor.isActive('strike') ? 'primary' : 'default'}
              icon={<StrikethroughOutlined />}
            />
          </Tooltip>
          <Tooltip title="Superscript">
            <Button
              onClick={() => editor.chain().focus().toggleSuperscript().run()}
              type={editor.isActive('superscript') ? 'primary' : 'default'}>
              x<sup>2</sup>
            </Button>
          </Tooltip>
        </Button.Group>
        <Divider type="vertical" />

        {/* Lists */}
        <Button.Group>
          <Tooltip title="Bullet List">
            <Button
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              type={editor.isActive('bulletList') ? 'primary' : 'default'}
              icon={<UnorderedListOutlined />}
            />
          </Tooltip>
          <Tooltip title="Numbered List">
            <Button
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              type={editor.isActive('orderedList') ? 'primary' : 'default'}
              icon={<OrderedListOutlined />}
            />
          </Tooltip>
        </Button.Group>
        <Divider type="vertical" />

        {/* Special elements */}
        <Button.Group>
          <Tooltip title="Blockquote">
            <Button
              disabled={true}
              onClick={() => editor.chain().focus().toggleBlockquote().run()}
              type={editor.isActive('blockquote') ? 'primary' : 'default'}>
              <FontAwesomeIcon icon={faQuoteRight} />
            </Button>
          </Tooltip>
          <Tooltip title={editor.isActive('tooltip') ? 'Edit/Remove Tooltip' : 'Add Tooltip'}>
            <Button
              onClick={handleTooltipClick}
              type={editor.isActive('tooltip') ? 'primary' : 'default'}
              icon={<InfoCircleOutlined />}
            />
          </Tooltip>
          <Tooltip title="Code">
            <Button
              onClick={() => editor.chain().focus().toggleCode().run()}
              type={editor.isActive('code') ? 'primary' : 'default'}
              icon={<CodeOutlined />}
            />
          </Tooltip>
          <Tooltip title={editor.isActive('link') ? 'Edit/Remove Link' : 'Add Link'}>
            <Button
              onClick={handleLinkClick}
              type={editor.isActive('link') ? 'primary' : 'default'}
              icon={<LinkOutlined />}
            />
          </Tooltip>
        </Button.Group>
      </Space>

      {/* Tooltip Modal */}
      <Modal
        title={editor.isActive('tooltip') ? 'Edit Tooltip' : 'Add Tooltip'}
        open={tooltipModalVisible}
        onOk={handleTooltipOk}
        onCancel={handleTooltipCancel}>
        <Input
          placeholder="Enter tooltip text"
          value={tooltipText}
          onChange={(e) => setTooltipText(e.target.value)}
          autoFocus
        />
      </Modal>

      {/* Link Modal */}
      <Modal
        title={editor.isActive('link') ? 'Edit Link' : 'Add Link'}
        open={linkModalVisible}
        onOk={handleLinkOk}
        onCancel={handleLinkCancel}>
        <Input
          placeholder="Enter URL (e.g., https://example.com)"
          value={linkUrl}
          onChange={(e) => setLinkUrl(e.target.value)}
          autoFocus
        />
      </Modal>
    </>
  );
};

export default MenuBar;
